// 商家设置时区
import dayjs from 'dayjs'
import http from '../http'
import { getSellerSettings } from './common'
import { calc } from '@yy/one-currency/libs/calc'
import { useMount } from 'ahooks'
import { useMemo, useState } from 'react'

let businessTimezone = dayjs.tz.guess()

let loadingPromise: Promise<void> = null
function fetchBusinessTimezone() {
  if (loadingPromise) return loadingPromise
  loadingPromise = getSellerSettings({ settingCodes: ['business_timezone'] })
    .then((sellerSetting) => {
      const json = sellerSetting?.data?.list?.[0]?.dataValue || '{}'
      businessTimezone = JSON.parse(json).timezone
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err)
    })
    .finally(() => {
      // 缓存5秒
      setTimeout(() => {
        loadingPromise = null
      }, 5000)
    })
  return loadingPromise
}

export async function getBusinessTimezone() {
  await fetchBusinessTimezone()
  return businessTimezone
}

export const updateTimezoneSetting = async (timezone: string) => {
  businessTimezone = timezone
  await http.admin.post('api/settings/updateTimezoneSetting', { businessTimezone: timezone })
  loadingPromise = null
  // 刷新一次
  fetchBusinessTimezone()
}

/**
 * 出参时间戳时区转换
 * 适用于时间戳都是我们系统写入的情况；
 * 为了让最终时间戳能按当前浏览器时区转换展示；（这是为了让展示组件对业务时区无感）
 * 先把时间戳按业务时区（businessTimezone）转成”2025-01-01 00:00:00.000“，然后按浏览器时区转成时间戳
 * @param time
 * @param timezone
 * @returns
 */
const formatOutTimezone = (time: number, timezone: string) => {
  if (time && timezone) {
    // 按业务时区转换成时间字符串
    const dateStr = dayjs(time).tz(timezone).format('YYYY-MM-DD HH:mm:ss.SSS')
    // 字符串按浏览器时区转换为时间戳
    return dayjs(dateStr).valueOf()
  }
  return time
}

/**
 * 入参时间戳时区转换
 * 入参时间戳通常是用户输入的”2025-01-01 00:00:00.000“字符串按当前浏览器时区转换出的时间戳；
 * 需要重新按当前浏览器时区转换成字符串，再按商家配置的业务时区（businessTimezone）转换回时间戳传给后端
 * @param time
 * @param timezone
 * @returns
 */
export const formatInTimezone = (time: number, timezone: string) => {
  if (time && timezone) {
    // 重新按当前浏览器时区转换成字符串
    const dateStr = dayjs(time).format('YYYY-MM-DD HH:mm:ss.SSS')
    // 再按商家配置的businessTimezone时区转换回时间戳
    return dayjs.tz(dateStr, timezone).valueOf()
  }
  return time
}

export function businessTimezoneTranslate<T extends Record<any, any>, R extends Record<any, any>>(
  func: (data: T, ...args: any) => Promise<R>,
  inKeys: string[],
  outKeys: string[]
): (data: T, ...args: any) => Promise<R> {
  return async (data: T) => {
    const timezone = await getBusinessTimezone()
    const newData = { ...data }
    // 入参转换
    travel(newData, inKeys, (val) => {
      return formatInTimezone(val, timezone)
    })
    const res = await func(newData)
    // 出参转换
    travel(res, outKeys, (val) => {
      return formatOutTimezone(val, timezone)
    })
    return res
  }
}

function travel(data, paths, calculate) {
  paths.forEach(function (path) {
    return calc(data, path, function (_, value) {
      if (Array.isArray(value)) {
        return value.map((v) => calculate(v))
      }
      return calculate(value)
    })
  })
}

export async function formatInTimezoneByKeys(data: any, inKeys: string[]) {
  const timezone = await getBusinessTimezone()
  // 入参转换
  travel(data, inKeys, (val) => {
    return formatInTimezone(val, timezone)
  })
}

export function useBusinessTimezone() {
  const [tz, setTZ] = useState<string>()
  useMount(async () => {
    const res = await getBusinessTimezone()
    setTZ(res)
  })
  return useMemo(
    () => ({
      timezone: tz,
      formatOutTimezone: (time: number) => formatOutTimezone(time, tz),
      formatInTimezone: (time: number) => formatInTimezone(time, tz),
    }),
    [tz]
  )
}
